import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  PersonAdd
} from '@mui/icons-material';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { API_URL } from '../config';

const RegisterPage = ({ onRegisterSuccess, onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Create Firebase account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get ID token
      const idToken = await user.getIdToken();
      
      // Verify with backend (backend also checks whitelist)
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          idToken: idToken,
          email: user.email,
          uid: user.uid
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // If backend says unauthorized email, show pending approval message
        if (errorData.code === 'UNAUTHORIZED_EMAIL') {
          setPendingApproval(true);
          setError('');
          await auth.signOut();  // Sign them out until approved
        } else {
          // Other errors - delete the account
          await user.delete();
          throw new Error(errorData.error || 'Registration rejected by server');
        }
        
        setLoading(false);
        return;
      }
      
      const loginResult = await response.json();
      
      // Store corpus ID in localStorage
      if (loginResult.corpus_id) {
        localStorage.setItem('corpus_id', loginResult.corpus_id);
        console.log('Corpus ID stored:', loginResult.corpus_id.substring(0, 8) + '...');
      } else {
        // Default to Skipper Demo for new users
        localStorage.setItem('corpus_id', 'demo-skipper-corpus-2024');
        console.log('✅ Set default corpus: Skipper Demo');
      }
      
      setSuccess(true);
      setError('');
      
      // Notify parent after short delay
      setTimeout(() => {
        onRegisterSuccess({
          name: user.email.split('@')[0],
          email: user.email,
          company: 'Skipper inc.',
          role: 'User',
          uid: user.uid
        });
      }, 2000);
      
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please login instead.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 8 characters with letters and numbers.');
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/assets/helm_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        p: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Helm Logo */}
        <Box sx={{ mb: 4 }}>
          <img 
            src="/assets/helm_logo.avif" 
            alt="Helm.ai" 
            style={{ height: 48, marginBottom: 16 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Join Helm Marketing Intelligence Platform
          </Typography>
        </Box>

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Account created successfully! Redirecting to dashboard...
          </Alert>
        )}
        
        {/* Pending Approval Alert */}
        {pendingApproval && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Account Created - Pending Approval
            </Typography>
            <Typography variant="body2">
              Your account has been created but needs admin approval. 
              Contact support to request access.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Email: <strong>{email}</strong>
            </Typography>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!success && !pendingApproval && (
          <>
            {/* Registration Form */}
            <Box component="form" onSubmit={handleRegister}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText="Anyone can register - access requires approval"
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText="Minimum 8 characters"
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
                required
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={<PersonAdd />}
                sx={{ 
                  mb: 3,
                  py: 1.5,
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Back to Login */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    onBackToLogin();
                  }}
                  sx={{ 
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </>
        )}
        
        {/* Show back to login if pending approval */}
        {pendingApproval && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={onBackToLogin}
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default RegisterPage;

